/* screens/settings/ProfileTab.jsx — Профиль аккаунта (window.SettingsProfileTab).
   Аватар (инициалы/фото) + имя + email (подтверждён / pending / смена) + дата регистрации.
   Данные из useAuth (боевая сессия); мутации — profileReal (имя/смена-отмена email/аватар).
   Аватар: multipart upload/delete → /api/auth/profile/avatar/; после — refreshUser() (аватар
   обновляется во всей сессии). // источник: 11 §2.2. ХЕНДОФФ → frontend/pages/settings/ProfileTab.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, Labeled, PasswordInput, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { TelegramLinkFlow } from '../../components/ui/TelegramLinkFlow.jsx';
import { profileReal } from '../../lib/api/index.js';
import * as authApi from '../../lib/api/auth.js';
import { fmt } from '../../lib/format.js';

  const { useState, useRef } = React;

  function Card({ title, hint, children }) {
    return (
      <section className="set-card">
        <div className="set-card__head"><h3 className="t-h4">{title}</h3>{hint && <span className="t-caption text-mute">{hint}</span>}</div>
        <div className="set-card__body">{children}</div>
      </section>
    );
  }

  function ProfileTab() {
    const { user, refreshUser } = useAuth();
    const toast = useToast();
    const confirm = useConfirm();
    // ХЕНДОФФ: в прототипе auth синхронный (user из CURRENT_USER уже есть на первом рендере).
    // В проде AuthContext грузится асинхронно → оборачивать раздел в QueryBoundary/гейт
    // авторизации выше, чтобы user никогда не был null здесь (иначе uncontrolled-инпуты).
    const u = user || {};
    const [name, setName] = useState(u.full_name || '');
    const [phone, setPhone] = useState(u.phone || '');
    const [savingName, setSavingName] = useState(false);
    const [pendingEmail, setPendingEmail] = useState(u.pending_email);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [curPw, setCurPw] = useState('');
    const [emailErr, setEmailErr] = useState(null);
    const [emailBusy, setEmailBusy] = useState(false);
    const [avatarBusy, setAvatarBusy] = useState(false);
    const fileRef = useRef(null);

    const dirty = name.trim() !== (u.full_name || '') || phone.trim() !== (u.phone || '');

    const saveProfile = async () => {
      setSavingName(true);
      try {
        await profileReal.updateProfile({ full_name: name.trim(), phone: phone.trim() });
        await refreshUser();
        toast.show('Профиль обновлён', 'success');
      } catch (e) { toast.show('Не удалось сохранить', 'danger'); }
      finally { setSavingName(false); }
    };

    const requestEmail = async () => {
      setEmailErr(null); setEmailBusy(true);
      try {
        const r = await profileReal.requestEmailChange({ new_email: newEmail.trim(), current_password: curPw });
        setPendingEmail(r.pending_email); setShowEmailForm(false); setNewEmail(''); setCurPw('');
        await refreshUser();
        toast.show('Письмо для подтверждения отправлено', 'success');
      } catch (e) {
        const map = { same_email: 'Это ваш текущий email', email_taken: 'Этот email уже занят', invalid_password: 'Неверный пароль', rate_limited: 'Слишком часто, попробуйте позже' };
        setEmailErr(map[e.code] || 'Не удалось запросить смену');
      } finally { setEmailBusy(false); }
    };

    const cancelPending = async () => {
      try { await profileReal.cancelEmailChange(); setPendingEmail(null); await refreshUser(); toast.show('Запрос на смену email отменён', 'muted'); }
      catch (e) { toast.show('Не удалось отменить', 'danger'); }
    };

    // Аватар: загрузка через скрытый file-input. Бэк валидирует размер/формат
    // (5 МБ, JPG/PNG/WEBP) и возвращает обновлённого user → refreshUser() обновляет сессию.
    const pickAvatar = () => { if (fileRef.current) fileRef.current.click(); };
    const onAvatarFile = async (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';                 // сброс — повторный выбор того же файла триггерит change
      if (!file) return;
      setAvatarBusy(true);
      try {
        await profileReal.uploadAvatar(file);
        await refreshUser();
        toast.show('Фото профиля обновлено', 'success');
      } catch (err) {
        toast.show((err && err.detail) || 'Не удалось загрузить фото', 'danger');
      } finally { setAvatarBusy(false); }
    };
    const removeAvatar = async () => {
      setAvatarBusy(true);
      try {
        await profileReal.deleteAvatar();
        await refreshUser();
        toast.show('Фото профиля удалено', 'muted');
      } catch (err) {
        toast.show('Не удалось удалить фото', 'danger');
      } finally { setAvatarBusy(false); }
    };

    // Telegram-привязка — через бота (TelegramLinkFlow): модалка сама ждёт Start в боте,
    // сюда приходит итог. Если этот Telegram принадлежал «спящему» перенесённому
    // аккаунту — его данные (чаты, подписка) уже слились сюда (merged).
    const tg = u.telegram;
    const [tgBusy, setTgBusy] = useState(false);
    const onTgLinked = async (res) => {
      toast.show(res && res.merged ? 'Telegram привязан — ваши чаты и подписка перенесены' : 'Telegram привязан', 'success');
      await refreshUser();
    };
    // Не удалось даже выдать ссылку (POST /telegram/link-request/).
    const onTgLinkError = (res) => {
      const code = res && res.data && res.data.code;
      if (code === 'already_linked') { toast.show('К аккаунту уже привязан Telegram', 'muted'); refreshUser(); }
      else if (code === 'telegram_not_configured') toast.show('Telegram-бот не настроен', 'danger');
      else toast.show((res && res.data && res.data.detail) || 'Не удалось начать привязку Telegram', 'danger');
    };
    // Отвязка: освобождает Telegram, чтобы привязать другой или совсем убрать.
    // Прошлый merge (перенесённые чаты/подписка) НЕ откатывается — данные остаются.
    const onTgUnlink = async () => {
      const ok = await confirm({
        title: 'Отвязать Telegram?',
        body: 'Telegram-рассылки перестанут на него приходить. Привязать заново (этот или другой) можно в любой момент. Перенесённые ранее чаты и подписка останутся.',
        confirmLabel: 'Отвязать', cancelLabel: 'Отмена', tone: 'danger',
      });
      if (!ok) return;
      setTgBusy(true);
      try {
        const res = await authApi.telegramUnlink();
        if (!res.ok) { toast.show('Не удалось отвязать Telegram', 'danger'); return; }
        toast.show('Telegram отвязан', 'success');
        await refreshUser();
      } catch (e) { toast.show('Не удалось отвязать Telegram', 'danger'); }
      finally { setTgBusy(false); }
    };

    return (
      <div className="settings-panel stack stack-16">
        <Card title="Аватар">
          <div className="avatar-row">
            <span className="avatar-lg" aria-hidden="true">
              {u.avatar_url ? <img src={u.avatar_url} alt="" /> : (u.initials || (u.full_name || '?').slice(0, 1))}
            </span>
            <div className="avatar-row__actions">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                hidden onChange={onAvatarFile} />
              <div className="cluster cluster-8">
                <Button variant="secondary" size="sm" iconLeft="upload" loading={avatarBusy} disabled={avatarBusy} onClick={pickAvatar}>{u.avatar_url ? 'Заменить' : 'Загрузить'}</Button>
                {u.avatar_url && <Button variant="ghost" size="sm" iconLeft="trash" disabled={avatarBusy} onClick={removeAvatar}>Удалить</Button>}
              </div>
              <p className="t-caption text-mute">JPG, PNG или WEBP до 5 МБ. Если фото нет — показываем инициалы.</p>
            </div>
          </div>
        </Card>

        <Card title="Имя и контакты">
          <div className="stack stack-12">
            <Field label="Полное имя">
              <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Как к вам обращаться" />
            </Field>
            <Field label="Телефон" hint="Необязательно. Используется в документах и карточке слушателя Академии.">
              <TextInput type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+7 999 123-45-67" />
            </Field>
            <div className="cluster cluster-8">
              <Button variant="primary" iconLeft="check" disabled={!dirty || savingName} loading={savingName} onClick={saveProfile}>Сохранить</Button>
            </div>
          </div>
        </Card>

        <Card title="Электронная почта">
          <div className="email-block">
            <div className="email-current">
              <span className="num">{u.email}</span>
              {u.email_verified
                ? <Pill tone="ok" size="sm">Подтверждён</Pill>
                : <Pill tone="warn" size="sm">Не подтверждён</Pill>}
            </div>

            {pendingEmail && (
              <div className="callout callout--info">
                <Icon name="clock" size={16} />
                <div className="grow">
                  <div className="t-label">Ожидает подтверждения</div>
                  <div className="t-caption text-dim">Мы отправили письмо на <span className="num">{pendingEmail}</span>. Перейдите по ссылке, чтобы завершить смену.</div>
                </div>
                <Button variant="ghost" size="sm" onClick={cancelPending}>Отменить</Button>
              </div>
            )}

            {!showEmailForm && !pendingEmail && (
              <Button variant="secondary" size="sm" iconLeft="mail" onClick={() => setShowEmailForm(true)}>Сменить email</Button>
            )}

            {showEmailForm && (
              <div className="email-form stack stack-12">
                <Field label="Новый email" required error={emailErr}>
                  <TextInput type="email" value={newEmail} invalid={!!emailErr} placeholder="new@example.com" onChange={(e) => { setNewEmail(e.target.value); setEmailErr(null); }} />
                </Field>
                <Field label="Текущий пароль" hint="Для подтверждения смены (демо-пароль: demo)">
                  <PasswordInput value={curPw} onChange={(e) => setCurPw(e.target.value)} placeholder="••••••••" />
                </Field>
                <div className="cluster cluster-8">
                  <Button variant="ghost" size="sm" onClick={() => { setShowEmailForm(false); setEmailErr(null); }}>Отмена</Button>
                  <Button variant="primary" size="sm" loading={emailBusy} disabled={emailBusy || !newEmail.trim() || !curPw} onClick={requestEmail}>Запросить смену</Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card title="Telegram">
          {tg ? (
            <div className="stack stack-8">
              <div className="set-card__ok">
                <div className="stack stack-2">
                  <span className="t-label">Привязан{tg.username ? ': @' + tg.username : ''}</span>
                  <span className="t-caption text-dim">Чтобы сменить Telegram — отвяжите текущий и привяжите другой.</span>
                </div>
                <Icon name="check-circle" size={18} />
              </div>
              <div style={{ display: 'flex' }}>
                <Button variant="ghost" size="sm" iconLeft="unlink" loading={tgBusy} disabled={tgBusy} onClick={onTgUnlink}>Отвязать Telegram</Button>
              </div>
            </div>
          ) : (
            <div className="stack stack-8">
              <span className="t-caption text-dim">
                Привяжите Telegram, если вы пользовались старой версией —
                ваши чаты и подписка подтянутся автоматически.
                Привязка идёт через бота: вы сами выбираете, с какого аккаунта Telegram открыть его.
              </span>
              <div style={{ display: 'flex' }}>
                <TelegramLinkFlow onLinked={onTgLinked} onError={onTgLinkError} />
              </div>
            </div>
          )}
        </Card>

        <Card title="Аккаунт">
          <div className="kv-grid">
            <Labeled label="Дата регистрации" value={u.date_joined ? fmt.date(u.date_joined) : '—'} />
            <Labeled label="ID пользователя" value={u.id != null ? String(u.id) : '—'} mono />
          </div>
        </Card>
      </div>
    );
  }

export { ProfileTab as SettingsProfileTab };
